import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { PdButton } from "../pd-button/PdButton.js";
import { css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
//#region src/pd-panel-button/PdPanelButton.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var panelIdCounter = 0;
/**
* Button with dropdown panel functionality. Extends PdButton with panel support.
*
* @tagname pd-panel-button
* @summary Button with dropdown panel that opens on click.
*
* @slot - Content displayed inside the dropdown panel.
*
* @event panel-button-toggled - Fired when panel opens or closes. Emits `{open: boolean}`.
*
* @cssprop --pd-panel-bg-col - Panel background color. Default: `--pd-default-bg-col`.
* @cssprop --pd-panel-min-width - Panel minimum width. Default: `auto`.
* @cssprop --pd-panel-max-width - Panel maximum width. Default: `300px`.
* @cssprop --pd-panel-padding - Panel content padding. Default: `1rem`.
* @cssprop --pd-panel-z-index - Panel stacking order. Default: `100`.
*/
var PdPanelButton = class extends PdButton {
	constructor(..._args) {
		super(..._args);
		this.hideClose = false;
		this.up = false;
		this.right = false;
		this._panelOpen = false;
		this._panelId = `pd-panel-${++panelIdCounter}`;
		this._boundClosePanel = this._closePanel.bind(this);
		this._boundHandleKeyDown = this._handlePanelKeyDown.bind(this);
	}
	static {
		this.styles = [PdButton.styles, css`
      :host {
        position: relative;
      }

      /* Toggle arrow icon styling - inherits color from .button-icon in PdButton */
      .toggle-icon {
        margin-left: calc(0.25rem * var(--pd-button-scale, 1));
      }

      /* Icon-only panel button: reduce padding since we have icon + toggle arrow */
      :host([icon-only]) button {
        padding: calc(0.5rem * var(--pd-button-scale, 1));
        aspect-ratio: auto;
        min-width: auto;
      }

      /* Panel container */
      .panel {
        position: absolute;
        min-width: var(--pd-panel-min-width, auto);
        max-width: var(--pd-panel-max-width, 300px);
        transform: scaleY(0);
        background: var(--pd-panel-bg-col, var(--pd-default-bg-col));
        border-radius: var(--pd-radius-lg);
        box-shadow: var(--pd-shadow-md);
        transition:
          transform 0.2s ease-out,
          opacity 0.2s ease-out;
        opacity: 0;
        z-index: var(--pd-panel-z-index, 100);
      }

      .panel.open {
        transform: scaleY(1);
        opacity: 1;
      }

      .panel-content {
        padding: var(--pd-panel-padding, 1rem);
        min-height: 50px;
      }

      /* Close button in panel */
      .close-button {
        position: absolute;
        top: 5px;
        right: 5px;
        background: transparent;
        border: none;
        color: var(--pd-default-dark-col);
        font-size: 1.2rem;
        cursor: pointer;
        padding: 5px;
        line-height: 1;
        transition: color 0.2s ease-in-out;
      }

      button.close-button:hover:not(:disabled) {
        background: transparent;
        color: var(--pd-default-hover-col);
      }

      /* Panel positioning variants */

      /* Default: opens downward, left-aligned */
      :host(:not([right]):not([up])) .panel {
        left: 0;
        top: 100%;
        transform-origin: top;
      }

      /* Right-aligned, opens downward */
      :host([right]:not([up])) .panel {
        right: 0;
        top: 100%;
        transform-origin: top right;
      }

      /* Left-aligned, opens upward */
      :host(:not([right])[up]) .panel {
        left: 0;
        bottom: 100%;
        transform-origin: bottom;
      }

      /* Right-aligned, opens upward */
      :host([right][up]) .panel {
        right: 0;
        bottom: 100%;
        transform-origin: bottom right;
      }
    `];
	}
	connectedCallback() {
		super.connectedCallback();
		window.addEventListener("close-button-panel-event", this._boundClosePanel);
		window.addEventListener("keydown", this._boundHandleKeyDown);
	}
	disconnectedCallback() {
		window.removeEventListener("close-button-panel-event", this._boundClosePanel);
		window.removeEventListener("keydown", this._boundHandleKeyDown);
		super.disconnectedCallback();
	}
	get buttonAriaExpanded() {
		return this._panelOpen ? "true" : "false";
	}
	get buttonAriaHasPopup() {
		return "true";
	}
	get buttonAriaControls() {
		return this._panelId;
	}
	/**
	* Renders the toggle arrow inside the button (as suffix content).
	* Uses activeIcon to switch between up/down arrow states.
	* activeIcon=true shows arrow up (panel open), activeIcon=false shows arrow down (panel closed).
	*/
	renderSuffixContent() {
		return html`
      <pd-icon
        icon="${pdIcons.ICON_TOOGLE_ARROW}"
        ?activeIcon="${!this._panelOpen}"
        class="button-icon toggle-icon"
      ></pd-icon>
    `;
	}
	/**
	* Renders the dropdown panel after the button.
	*/
	renderAfterButton() {
		return html`
      <div
        id="${this._panelId}"
        class="panel ${this._panelOpen ? "open" : ""}"
        role="menu"
        aria-hidden="${!this._panelOpen}"
        ?inert="${!this._panelOpen}"
      >
        ${this.hideClose ? nothing : html`
                <button
                  class="close-button"
                  @click="${this._togglePanel}"
                  @keydown="${this._handleCloseButtonKeyDown}"
                  aria-label="Close panel"
                >
                  &times;
                </button>
              `}
        <div class="panel-content">
          <slot></slot>
        </div>
      </div>
    `;
	}
	/**
	* Override: Click toggles the panel instead of emitting button-clicked.
	*/
	handleClick() {
		if (!this.disabled && !this.loading) this._togglePanel();
	}
	/** Focuses the internal button element. */
	focus() {
		this._buttonEl?.focus();
	}
	_closePanel() {
		this._panelOpen = false;
	}
	_togglePanel() {
		this._panelOpen = !this._panelOpen;
		this.dispatchEvent(new CustomEvent("panel-button-toggled", {
			detail: { open: this._panelOpen },
			bubbles: true,
			composed: true
		}));
	}
	/** Handles Escape key to close the panel */
	_handlePanelKeyDown(e) {
		if (e.key === "Escape" && this._panelOpen) {
			this._closePanel();
			this._buttonEl?.focus();
		}
	}
	/** Handles keyboard events on the close button */
	_handleCloseButtonKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._togglePanel();
		}
	}
};
__decorate([property({
	type: Boolean,
	attribute: "hide-close"
})], PdPanelButton.prototype, "hideClose", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdPanelButton.prototype, "up", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdPanelButton.prototype, "right", void 0);
__decorate([state()], PdPanelButton.prototype, "_panelOpen", void 0);
__decorate([query("button")], PdPanelButton.prototype, "_buttonEl", void 0);
//#endregion
export { PdPanelButton };
